/**
 * AdminPageNumberBadge
 * ─────────────────────
 * A floating "page #N" badge visible ONLY to the two BareMC admins
 * (Tim & Werby). It exists so Tim can tell the agent "fix something
 * on side 7" instead of trying to describe a URL, route name, or
 * screenshot location.
 *
 * Rules:
 *  • Only renders when `user.is_site_admin` is true (server-side flag
 *    already set for timmotto@gmail.com & werby@theothers.dk).
 *  • Mounts ONCE at app level — picks the page number from the
 *    current `useLocation().pathname`.
 *  • Stays out of the way: bottom-right, semi-transparent, tappable
 *    only to copy "side #N – /path" to the clipboard for fast pasting
 *    into chat with the agent.
 *  • Stable numbering: explicit map below. New routes get appended.
 *    Never renumber existing entries — Tim's memory of "side 4 =
 *    Klubber" should keep working.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// ROUTE → STABLE PAGE NUMBER. APPEND-ONLY.
// Order roughly mirrors how Tim navigates the app from front to admin.
const PAGE_NUMBERS = [
  // ── Public ──
  { number: 1,  label: 'Startside',            match: '/' },
  { number: 2,  label: 'Kort',                 match: '/kort' },
  { number: 3,  label: 'Klubber (forside)',    match: '/clubs' },
  { number: 4,  label: 'Klubber (mine)',       match: '/klubber' },
  { number: 5,  label: 'Klub detalje',         match: '/klubber/:id' },
  { number: 6,  label: 'Klub offentlig',       match: '/club/:slug' },
  { number: 7,  label: 'Event detalje',        match: '/events/:id' },
  { number: 8,  label: 'Event offentlig',      match: '/event/:slug' },
  { number: 9,  label: 'Events oversigt',      match: '/events' },
  { number: 10, label: 'Pitstop detalje',      match: '/pitstop/:id' },
  { number: 11, label: 'Overnatning detalje', match: '/accommodation/:id' },
  { number: 12, label: 'Meetup detalje',       match: '/meetup/:id' },
  { number: 13, label: 'Tilbud',               match: '/tilbud' },
  { number: 14, label: 'Turvideoer',           match: '/reels' },
  { number: 15, label: 'Public turvideo',      match: '/r/:postId' },
  { number: 16, label: 'Discover',             match: '/discover' },
  { number: 17, label: 'Leaderboard',          match: '/leaderboard' },
  { number: 18, label: 'Nummerplade',          match: '/nummerplade' },
  // ── Auth flows ──
  { number: 19, label: 'Log ind',              match: '/login' },
  { number: 20, label: 'Opret konto',          match: '/register' },
  { number: 21, label: 'Glemt adgangskode',    match: '/glemt-adgangskode' },
  { number: 22, label: 'Nulstil password',     match: '/reset-password' },
  // ── User space ──
  { number: 23, label: 'Feed',                 match: '/feed' },
  { number: 24, label: 'Dashboard',            match: '/dashboard' },
  { number: 25, label: 'Konto',                match: '/konto' },
  { number: 26, label: 'Beskeder',             match: '/beskeder' },
  { number: 27, label: 'Mine beskeder',        match: '/mine-beskeder' },
  { number: 28, label: 'Besked tråd',          match: '/mine-beskeder/:threadId' },
  { number: 29, label: 'Profil',               match: '/profil/:userId' },
  { number: 30, label: 'Favoritter',           match: '/favoritter' },
  { number: 31, label: 'Mine ture',            match: '/mine-ture' },
  { number: 32, label: 'Plan tur',             match: '/plan-tur' },
  { number: 33, label: 'Replay Lab',           match: '/replay-lab/:rideId' },
  // ── Create flows ──
  { number: 34, label: 'Opret event',          match: '/opret-event' },
  { number: 35, label: 'Opret meetup',         match: '/opret-meetup' },
  { number: 36, label: 'Opret pitstop',        match: '/opret-pitstop' },
  { number: 37, label: 'Pitstop ansøg',        match: '/pitstop-ansoeg' },
  { number: 38, label: 'Opret overnatning',    match: '/opret-overnatning' },
  { number: 39, label: 'Overnatning ansøg',    match: '/overnatning-ansoeg' },
  // ── Settings & info ──
  { number: 40, label: 'Indstillinger',        match: '/indstillinger' },
  { number: 41, label: 'Hjælp',                match: '/hjaelp' },
  { number: 42, label: 'Regler',               match: '/regler' },
  { number: 43, label: 'Kontakt',              match: '/kontakt' },
  { number: 44, label: 'Afmeld',               match: '/afmeld' },
  // ── Admin ──
  { number: 45, label: 'Admin',                match: '/admin' },
  { number: 46, label: 'Admin indbakke',       match: '/admin-indbakke' },
  { number: 47, label: 'Admin indhold',        match: '/admin/indhold' },
  { number: 48, label: 'Superadmin',           match: '/superadmin' },
  { number: 49, label: 'Farli',                match: '/farli' },
];

function resolvePageNumber(pathname) {
  // Exact match wins over template match — '/feed' must hit the explicit
  // entry, not a hypothetical '/:slug'.
  const exact = PAGE_NUMBERS.find(p => p.match === pathname);
  if (exact) return exact;
  for (const p of PAGE_NUMBERS) {
    if (p.match.includes(':') && matchPath(p.match, pathname)) return p;
  }
  return null;
}

const AdminPageNumberBadge = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  const page = useMemo(() => resolvePageNumber(location.pathname), [location.pathname]);

  // Stamp the page number on <body> for EVERY visitor — not just
  // admins. When a user sends a screenshot, console log, or network
  // trace, the agent can read `data-page-number` and immediately know
  // which route the issue came from without guessing from URLs. Tim
  // verified: alle problemer kommer fra baremc.dk, så vi sætter det
  // globalt.
  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) return;
    if (page) {
      document.body.dataset.pageNumber = String(page.number);
      document.body.dataset.pageLabel = page.label;
    } else {
      // Unknown route — still mark it so support can see "page 0"
      // means "we haven't mapped this URL yet".
      document.body.dataset.pageNumber = '0';
      document.body.dataset.pageLabel = 'ukendt';
    }
  }, [page]);

  // Only the 2 site admins ever see the visible BADGE. Everyone else
  // still gets the body data-attribute (above) for debugging.
  if (!user?.is_site_admin) return null;
  if (!page) return null;

  const handleCopy = async (e) => {
    e.stopPropagation();
    const text = `side ${page.number} (${page.label}) – ${location.pathname}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard denied in some PWAs — silent */ }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid="admin-page-number-badge"
      aria-label={`Side ${page.number}: ${page.label}. Tryk for at kopiere.`}
      style={{
        position: 'fixed',
        right: 8,
        bottom: 88, // sit above the mobile bottom nav (~64px) + buffer
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 999,
        background: copied ? 'rgba(16,185,129,0.92)' : 'rgba(15,15,15,0.78)',
        color: copied ? '#062b1f' : '#ffd76b',
        border: '1px solid rgba(255,215,107,0.45)',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        cursor: 'pointer',
        // tiny — never blocks UI
        maxWidth: 200,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 18,
        borderRadius: 9,
        background: copied ? '#062b1f' : '#ffd76b',
        color: copied ? '#ffd76b' : '#1a1a1a',
        fontSize: 11,
        padding: '0 6px',
      }}>
        #{page.number}
      </span>
      {copied ? 'kopieret' : page.label}
    </button>
  );
};

export default AdminPageNumberBadge;
